import {
  EraserIcon,
  PenToolIcon,
  Trash2Icon,
  Maximize2Icon,
  XIcon,
  CheckIcon,
} from 'lucide-react'
import React, {
  useEffect,
  useId,
  useRef,
  useState,
  useCallback,
} from 'react'
import {
  CanvasPath,
  ReactSketchCanvas,
  ReactSketchCanvasRef,
} from 'react-sketch-canvas'
import { createPortal } from 'react-dom'
import { Label } from './label'

const FIT_ON_RETURN = true

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(query)
    const handler = () => setMatches(mq.matches)
    handler()
    mq.addEventListener?.('change', handler)
    return () => mq.removeEventListener?.('change', handler)
  }, [query])
  return matches
}

type XY = { x: number; y: number }
type PathLike = CanvasPath & { paths: XY[] }

function scaleAndCenterPaths(
  paths: CanvasPath[],
  fromW: number,
  fromH: number,
  toW: number,
  toH: number
): CanvasPath[] {
  if (!fromW || !fromH || !toW || !toH) return paths
  const s = Math.min(toW / fromW, toH / fromH)
  const offX = (toW - fromW * s) / 2
  const offY = (toH - fromH * s) / 2
  return (paths as PathLike[]).map((p) => ({
    ...p,
    strokeWidth: (p.strokeWidth ?? 1) * s,
    paths: p.paths.map((pt) => ({
      x: pt.x * s + offX,
      y: pt.y * s + offY,
    })),
  }))
}

// NEW: same as scaleAndCenterPaths, but never shrink.
// s is clamped so it's at least 1.0.
function scaleAndCenterPathsClampUp(
  paths: CanvasPath[],
  fromW: number,
  fromH: number,
  toW: number,
  toH: number
): CanvasPath[] {
  if (!fromW || !fromH || !toW || !toH) return paths

  // "fit" scale like before
  let s = Math.min(toW / fromW, toH / fromH)

  // IMPORTANT: never go below 1 when going fullscreen.
  if (s < 1) s = 1

  const offX = (toW - fromW * s) / 2
  const offY = (toH - fromH * s) / 2

  return (paths as PathLike[]).map((p) => ({
    ...p,
    strokeWidth: (p.strokeWidth ?? 1) * s,
    paths: p.paths.map((pt) => ({
      x: pt.x * s + offX,
      y: pt.y * s + offY,
    })),
  }))
}

// keep --vvh in sync for fullscreen height on mobile
function useVisualViewportVar() {
  useEffect(() => {
    const setVar = () => {
      const h = window.visualViewport?.height ?? window.innerHeight
      document.documentElement.style.setProperty('--vvh', `${h}px`)
    }
    setVar()
    window.visualViewport?.addEventListener('resize', setVar)
    window.addEventListener('resize', setVar)
    window.addEventListener('orientationchange', setVar)
    return () => {
      window.visualViewport?.removeEventListener('resize', setVar)
      window.removeEventListener('resize', setVar)
      window.removeEventListener('orientationchange', setVar)
    }
  }, [])
}

type SignatureProps = {
  label?: string
  onChange?: (signatureBase64: string) => void
  onChangePaths?: (paths: CanvasPath[]) => void
  defaultPath?: CanvasPath[]
  height?: number | string
  mobileHeight?: number | string
  className?: string
  expandableOnMobile?: boolean
}

const Signature = ({
  label = 'Handtekening',
  onChange,
  onChangePaths,
  defaultPath,
  height = 300,
  mobileHeight = '50vh',
  className = '',
  expandableOnMobile = false,
}: SignatureProps) => {
  const sketchRef = useRef<ReactSketchCanvasRef | null>(null)
  const applyingPathsRef = useRef(false)
  const loadedDefaultRef = useRef(false)

  const [eraserMode, setEraserMode] = useState(false)
  const isMobile = useMediaQuery('(max-width: 640px)')
  const [isFullscreen, setIsFullscreen] = useState(false)

  // snapshot of inline before going fullscreen
  const [preFullscreenPaths, setPreFullscreenPaths] = useState<CanvasPath[] | null>(null)

  // pre-upscaled paths we want to load into fullscreen on mount
  const [pendingFullscreenInitialPaths, setPendingFullscreenInitialPaths] =
    useState<CanvasPath[] | null>(null)

  // after closing fullscreen with "Gereed"
  const [pendingFullscreenSave, setPendingFullscreenSave] = useState<{
    paths: CanvasPath[]
    fromW: number
    fromH: number
  } | null>(null)

  // after closing fullscreen with "Annuleren"
  const [pendingFullscreenCancel, setPendingFullscreenCancel] = useState<
    CanvasPath[] | null
  >(null)

  // wrappers and measured sizes
  const inlineBoxRef = useRef<HTMLDivElement | null>(null)
  const [inlineBoxSize, setInlineBoxSize] = useState({ w: 0, h: 0 })

  const fullscreenBoxRef = useRef<HTMLDivElement | null>(null)
  const [fullscreenBoxSize, setFullscreenBoxSize] = useState({ w: 0, h: 0 })

  useVisualViewportVar()

  // observe both wrappers for size
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const cr = e.contentRect
        if (e.target === inlineBoxRef.current) {
          setInlineBoxSize({ w: cr.width, h: cr.height })
        }
        if (e.target === fullscreenBoxRef.current) {
          setFullscreenBoxSize({ w: cr.width, h: cr.height })
        }
      }
    })
    if (inlineBoxRef.current) ro.observe(inlineBoxRef.current)
    if (fullscreenBoxRef.current) ro.observe(fullscreenBoxRef.current)
    return () => ro.disconnect()
  }, [])

  // choose correct height
  const rawHeight = isFullscreen
    ? 'calc(var(--vvh, 100dvh) - 56px)'
    : isMobile
    ? mobileHeight
    : height
  const canvasHeight: string =
    typeof rawHeight === 'number' ? `${rawHeight}px` : rawHeight

  const inputId = useId()

  // send base64 + paths up to parent, but guard first stroke
  const convertToBase = useCallback(async () => {
    if (!sketchRef.current || applyingPathsRef.current) return

    const exportBase = await sketchRef.current.exportImage('jpeg')
    onChange?.(exportBase)

    if (onChangePaths) {
      try {
        const paths = await sketchRef.current.exportPaths()
        onChangePaths(paths)
      } catch {
        // first-stroke race from react-sketch-canvas, ignore
      }
    }
  }, [onChange, onChangePaths])

  // keep eraser mode synced
  useEffect(() => {
    if (sketchRef.current) {
      sketchRef.current.eraseMode(eraserMode)
    }
  }, [eraserMode])

  function clearSketch() {
    sketchRef.current?.clearCanvas()
  }

  async function loadSketch(paths: CanvasPath[]) {
    if (!sketchRef.current) return
    applyingPathsRef.current = true
    await sketchRef.current.clearCanvas()
    await sketchRef.current.loadPaths(paths)
    applyingPathsRef.current = false
  }

  // initial default load
  useEffect(() => {
    if (!loadedDefaultRef.current && defaultPath && defaultPath.length > 0) {
      loadedDefaultRef.current = true
      loadSketch(defaultPath)
    }
  }, [defaultPath])

  // lock scroll when fullscreen
  useEffect(() => {
    if (!isFullscreen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isFullscreen])

  // estimate fullscreen box before mounting it
  function estimateFullscreenSize() {
    // fullscreen canvas is essentially viewport minus ~56px header
    const estViewportH =
      (window.visualViewport?.height ?? window.innerHeight) - 56
    const estViewportW = window.innerWidth

    // safety fallback in case browser lies
    const w = estViewportW > 0 ? estViewportW : fullscreenBoxSize.w
    const h = estViewportH > 0 ? estViewportH : fullscreenBoxSize.h

    return { w, h }
  }

  // OPEN FULLSCREEN:
  // 1. grab inline paths
  // 2. pre-scale them UP using clampUp logic so they fill fullscreen
  // 3. store that as pendingFullscreenInitialPaths (what fullscreen should show immediately)
  // 4. keep original in preFullscreenPaths (for Annuleren)
  // 5. show fullscreen
  const openFullscreen = useCallback(async () => {
    let currentPaths: CanvasPath[] = []
    if (sketchRef.current) {
      currentPaths = await sketchRef.current.exportPaths()
    }

    // snapshot for cancel restore
    setPreFullscreenPaths(currentPaths)

    // compute fullscreen "target" size BEFORE actually rendering fullscreen canvas
    const est = estimateFullscreenSize()

    let upscaled = currentPaths
    if (
      FIT_ON_RETURN &&
      inlineBoxSize.w > 0 &&
      inlineBoxSize.h > 0 &&
      est.w > 0 &&
      est.h > 0
    ) {
      // use clampUp so it never shrinks when going fullscreen
      upscaled = scaleAndCenterPathsClampUp(
        currentPaths,
        inlineBoxSize.w,
        inlineBoxSize.h,
        est.w,
        est.h
      )
    }

    setPendingFullscreenInitialPaths(upscaled)

    setIsFullscreen(true)
  }, [inlineBoxSize.w, inlineBoxSize.h])

  // CLOSE FULLSCREEN:
  // save = Gereed → scale DOWN later
  // !save = Annuleren → restore snapshot
  const closeFullscreen = useCallback(
    async (save: boolean) => {
      if (!sketchRef.current) {
        setIsFullscreen(false)
        return
      }

      if (save) {
        const paths = await sketchRef.current.exportPaths()

        // measure actual fullscreen box now (after it's mounted)
        let fromW = fullscreenBoxSize.w
        let fromH = fullscreenBoxSize.h
        if (fullscreenBoxRef.current) {
          const rect = fullscreenBoxRef.current.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            fromW = rect.width
            fromH = rect.height
          }
        }

        setPendingFullscreenSave({
          paths,
          fromW,
          fromH,
        })
      } else {
        setPendingFullscreenCancel(preFullscreenPaths ?? [])
      }

      setIsFullscreen(false)
    },
    [fullscreenBoxSize.w, fullscreenBoxSize.h, preFullscreenPaths]
  )

  // AFTER fullscreen closes and inline is visible:
  // - cancel → reload snapshot
  // - save → scale DOWN fullscreen drawing into inline
  const applyPendingRestore = useCallback(async () => {
    if (isFullscreen) return

    // cancel restore
    if (pendingFullscreenCancel && pendingFullscreenCancel.length > 0) {
      await loadSketch(pendingFullscreenCancel)
      await convertToBase()
      setPendingFullscreenCancel(null)
      return
    }

    // save restore
    if (
      pendingFullscreenSave &&
      inlineBoxSize.w > 0 &&
      inlineBoxSize.h > 0
    ) {
      const { paths, fromW, fromH } = pendingFullscreenSave

      let toLoad = paths
      if (
        FIT_ON_RETURN &&
        fromW > 0 &&
        fromH > 0
      ) {
        toLoad = scaleAndCenterPaths(
          paths,
          fromW,
          fromH,
          inlineBoxSize.w,
          inlineBoxSize.h
        )
      }

      await loadSketch(toLoad)
      await convertToBase()
      setPendingFullscreenSave(null)
    }
  }, [
    isFullscreen,
    inlineBoxSize.w,
    inlineBoxSize.h,
    pendingFullscreenCancel,
    pendingFullscreenSave,
    loadSketch,
    convertToBase,
  ])

  useEffect(() => {
    applyPendingRestore()
  }, [applyPendingRestore])

  // WHEN fullscreen mounts: immediately load the precomputed fullscreen version
  useEffect(() => {
    if (!isFullscreen) return
    if (!pendingFullscreenInitialPaths) return

    ;(async () => {
      await loadSketch(pendingFullscreenInitialPaths)
      // don't clear preFullscreenPaths, we still use it for cancel
      setPendingFullscreenInitialPaths(null)
    })()
  }, [isFullscreen, pendingFullscreenInitialPaths])

  // UI for toolbar + canvas
  const CanvasUI = (
    <>
      <div className="flex items-end justify-between">
        <Label className="mb-1" htmlFor={inputId}>
          {label}
        </Label>
        <div className="flex items-end">
          {/* pen */}
          <button
            className={`${
              !eraserMode ? 'text-orange-600' : ''
            } w-8 h-8 sm:w-7 sm:h-7 rounded-sm flex justify-center items-center text-neutral-400`}
            onClick={() => setEraserMode(false)}
            type="button"
            aria-label="Pen"
            title="Pen"
          >
            <PenToolIcon className="w-4" />
          </button>

          {/* eraser */}
          <button
            className={`${
              eraserMode ? 'text-orange-600' : ''
            } w-8 h-8 sm:w-7 sm:h-7 rounded-sm flex justify-center items-center text-neutral-400`}
            onClick={() => setEraserMode((prev) => !prev)}
            type="button"
            aria-label="Eraser"
            title="Gum"
          >
            <EraserIcon className="w-4" />
          </button>

          {/* clear */}
          <button
            className="w-8 h-8 sm:w-7 sm:h-7 rounded-sm flex justify-center items-center text-neutral-400"
            onClick={clearSketch}
            type="button"
            aria-label="Clear"
            title="Leegmaken"
          >
            <Trash2Icon className="w-4" />
          </button>

          {/* fullscreen trigger */}
          {expandableOnMobile && isMobile && !isFullscreen && (
            <button
              className="w-8 h-8 sm:w-7 sm:h-7 rounded-sm flex justify-center items-center text-neutral-400"
              onClick={openFullscreen}
              type="button"
              aria-label="Fullscreen"
              title="Volledig scherm"
            >
              <Maximize2Icon className="w-4" />
            </button>
          )}
        </div>
      </div>

      <div className={`${isFullscreen ? '' : '-mx-4 sm:mx-0'}`}>
        {!isFullscreen ? (
          <div
            ref={inlineBoxRef}
            className="w-full"
            style={{ height: canvasHeight }}
          >
            <ReactSketchCanvas
              onChange={convertToBase}
              ref={sketchRef}
              eraserWidth={15}
              height="100%"
              strokeWidth={4}
              strokeColor="#014331"
              className="border w-full !border-input touch-none h-full bg-white"
              id={inputId}
            />
          </div>
        ) : (
          <div
            ref={fullscreenBoxRef}
            className="w-full"
            style={{ height: canvasHeight }}
          >
            <ReactSketchCanvas
              onChange={convertToBase}
              ref={sketchRef}
              eraserWidth={15}
              height="100%"
              strokeWidth={4}
              strokeColor="#014331"
              className="border w-full !border-input touch-none h-full bg-white"
              id={inputId}
            />
          </div>
        )}
      </div>
    </>
  )

  return (
    <div className={className}>
      {!isFullscreen && <div>{CanvasUI}</div>}

      {isFullscreen &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999] bg-white flex flex-col"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className="h-14 flex items-center justify-between px-3 border-b">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border"
                  onClick={() => closeFullscreen(false)}
                >
                  <XIcon className="w-4 h-4" />
                  <span>Annuleren</span>
                </button>
              </div>
              <div className="font-medium">{label}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-emerald-600 text-white"
                  onClick={() => closeFullscreen(true)}
                >
                  <CheckIcon className="w-4 h-4" />
                  <span>Gereed</span>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden px-0">
              <div className="h-full">{CanvasUI}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

export default Signature

export async function _base64ToCanvas(
  base64: string
): Promise<CanvasPath[]> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = base64
    img.onload = () => {
      const now = Date.now()
      const paths: CanvasPath[] = [
        {
          paths: [
            { x: 0, y: 0 },
            { x: img.width, y: 0 },
            { x: img.width, y: img.height },
            { x: 0, y: img.height },
            { x: 0, y: 0 },
          ],
          strokeWidth: 2,
          strokeColor: 'black',
          drawMode: true,
          startTimestamp: now,
          endTimestamp: now + 1000,
        },
      ]
      resolve(paths)
    }
    img.onerror = (e) => reject(e)
  })
}
